import type { DatosDeRolDeTerreno, SeccionDeTerreno } from "@/lib/terreno";

import { formatearSuperficie, parsearSuperficie } from "./superficies";

/* ---------------------------------------------------------------------------
   Los roles que la App de terreno propone, en la tabla "Roles del informe".

   Lógica PURA, fuera del componente: decidir si una propuesta "ya está
   aplicada" o si un rol de terreno "ya está en los roles" es lo que dice qué
   ve la persona, y dentro del árbol un test no distingue una comparación
   correcta de una cercana (30 contra "30,00", hectáreas contra metros).

   Nada acá escribe: las propuestas son observaciones del encargado y entran al
   formulario como cualquier cambio sin guardar.
--------------------------------------------------------------------------- */

export type DatosDeRol = DatosDeRolDeTerreno;

export type UnidadDelInforme = "ha" | "m2";

/** Los valores de una fila de la tabla, tal como los guarda el formulario. */
export interface ValoresDeRol {
  rol_sii: string;
  comuna_codigo: string;
  comuna: string;
  superficie: string;
  superficie_construida: string;
}

/** Lo que cambiaría en la fila: solo las claves que la propuesta trae y que difieren. */
export type CambiosDeRol = Partial<ValoresDeRol>;

export interface PropuestaDeRol {
  datos: DatosDeRol;
  por: string | null;
  capturadaEn: string | null;
}

export interface RolNuevoDeTerreno {
  referencia: string;
  datos: DatosDeRol;
  por: string | null;
}

const M2_POR_HA = 10_000;

/**
 * La superficie de terreno de terreno viene en hectáreas; el informe la lleva
 * en SU unidad. Redondea a centésimas para no arrastrar `0.1 * 10000` en punto
 * flotante hasta el campo.
 */
function terrenoEnLaUnidad(hectareas: number, unidad: UnidadDelInforme): number {
  return unidad === "m2" ? Math.round(hectareas * M2_POR_HA * 100) / 100 : hectareas;
}

function centesimas(valor: number | null): number | null {
  return valor === null ? null : Math.round(valor * 100);
}

const texto = (valor: string | null | undefined) => (valor ?? "").trim();

/** Una clave de rol para comparar: sin espacios ni mayúsculas. */
const claveDeRol = (valor: string | null | undefined) => texto(valor).toLowerCase();

/**
 * Qué campos de la fila cambiaría "Usar datos de terreno".
 *
 * Solo entran los que la propuesta TRAE (un `null` es "el encargado no tocó
 * esto", no "bórralo") y que además difieren de lo que la fila ya tiene; vacío
 * significa que la propuesta ya está aplicada. La comuna viaja entera, código
 * y nombre: el selector se resuelve por el código y el nombre es el que el
 * servidor vuelve a derivar al guardar.
 */
export function cambiosDeLaPropuesta(
  propuesta: DatosDeRol,
  fila: ValoresDeRol,
  unidad: UnidadDelInforme,
): CambiosDeRol {
  const cambios: CambiosDeRol = {};

  if (propuesta.rol_sii !== null && claveDeRol(propuesta.rol_sii) !== claveDeRol(fila.rol_sii)) {
    cambios.rol_sii = texto(propuesta.rol_sii);
  }

  if (propuesta.comuna_codigo !== null && texto(propuesta.comuna_codigo) !== texto(fila.comuna_codigo)) {
    cambios.comuna_codigo = texto(propuesta.comuna_codigo);
    cambios.comuna = texto(propuesta.comuna);
  }

  if (propuesta.superficie !== null) {
    const propuesto = terrenoEnLaUnidad(propuesta.superficie, unidad);

    if (centesimas(propuesto) !== centesimas(parsearSuperficie(fila.superficie))) {
      cambios.superficie = String(propuesto);
    }
  }

  if (propuesta.superficie_construida !== null) {
    if (
      centesimas(propuesta.superficie_construida) !==
      centesimas(parsearSuperficie(fila.superficie_construida))
    ) {
      cambios.superficie_construida = String(propuesta.superficie_construida);
    }
  }

  return cambios;
}

/** "Rol 245-12 · Curicó · 30 ha · 180 m²", solo con lo que difiere. */
export function textoDeLaPropuesta(propuesta: DatosDeRol, cambios: CambiosDeRol): string {
  const partes: string[] = [];

  if (cambios.rol_sii !== undefined) partes.push(`Rol ${texto(propuesta.rol_sii)}`);
  if (cambios.comuna_codigo !== undefined && texto(propuesta.comuna) !== "") {
    partes.push(texto(propuesta.comuna));
  }
  if (cambios.superficie !== undefined && propuesta.superficie !== null) {
    partes.push(`${formatearSuperficie(propuesta.superficie)} ha`);
  }
  if (cambios.superficie_construida !== undefined && propuesta.superficie_construida !== null) {
    partes.push(`${formatearSuperficie(propuesta.superficie_construida)} m²`);
  }

  return partes.join(" · ");
}

/** Los valores de un rol de terreno como los espera una fila nueva del formulario. */
export function valoresDeFila(datos: DatosDeRol, unidad: UnidadDelInforme): ValoresDeRol {
  return {
    rol_sii: texto(datos.rol_sii),
    comuna_codigo: texto(datos.comuna_codigo),
    comuna: texto(datos.comuna),
    superficie: datos.superficie === null ? "" : String(terrenoEnLaUnidad(datos.superficie, unidad)),
    superficie_construida:
      datos.superficie_construida === null ? "" : String(datos.superficie_construida),
  };
}

/**
 * ¿Ya hay en la tabla una fila con este mismo rol y esta misma comuna?
 *
 * Es la unidad que reconoce el SII (el rol es único DENTRO de su comuna). Un
 * rol de terreno sin número no se puede reconocer en ninguna fila.
 */
export function yaEstaEnLosRoles(
  datos: DatosDeRol,
  filas: Pick<ValoresDeRol, "rol_sii" | "comuna_codigo">[],
): boolean {
  if (claveDeRol(datos.rol_sii) === "") return false;

  return filas.some(
    (fila) =>
      claveDeRol(fila.rol_sii) === claveDeRol(datos.rol_sii) &&
      texto(fila.comuna_codigo) === texto(datos.comuna_codigo),
  );
}

/**
 * Lo que la sección «Antecedentes generales» del registro de terreno dice de
 * los roles: las propuestas sobre roles de la web (por id) y los roles que el
 * encargado creó en el predio.
 *
 * Un rol de terreno no trae autor propio: se atribuye al encargado del
 * registro.
 */
export function rolesDeTerreno(
  seccion: SeccionDeTerreno | null,
  encargado: string | null,
): { propuestas: Map<number, PropuestaDeRol>; nuevos: RolNuevoDeTerreno[] } {
  const propuestas = new Map<number, PropuestaDeRol>();
  const nuevos: RolNuevoDeTerreno[] = [];

  for (const item of seccion?.items ?? []) {
    if (item.referencia.startsWith("rol:")) {
      const id = Number(item.referencia.slice("rol:".length));

      if (Number.isInteger(id) && item.propuesta) {
        propuestas.set(id, {
          datos: item.propuesta,
          por: item.propuesta_por ?? null,
          capturadaEn: item.propuesta_capturada_en ?? null,
        });
      }
    } else if (item.referencia.startsWith("rol-terreno:") && item.rol) {
      nuevos.push({ referencia: item.referencia, datos: item.rol, por: encargado });
    }
  }

  return { propuestas, nuevos };
}
